// assignmentModel.ts
//
// Base layer of the raid sheet: who is in the raid, what each class can do,
// and how a filled board is computed. The night-role and depth-chart layer
// lives in nightRoles.ts and builds on this one, never the other way round,
// so there is no import cycle.
//
// A raid night is a grid of BOSSES x DUTY LANES, and every lane owns a fixed
// set of SLOTS ("Main Tank", "Interrupt 2nd", "Decurse 1"). Each slot carries
// an eligibility rule, so the board prefills itself from the planner roster:
// roles come from guild_raid_assignment.role_label, class utility (kick,
// decurse, tranq, MD, battle rez) from the class table below.
//
// A filled slot resolves through three layers, in order:
//   1. per-slot override  — this boss only
//   2. night role         — set once on the setup sheet, carried everywhere
//   3. auto-fill          — from the guild roster and class abilities

// ── Roles ────────────────────────────────────────────────────
export type RoleKey = "tank" | "healer" | "melee" | "ranged";

export const ROLE_META: Record<RoleKey, { label: string; short: string; color: string; glyph: string }> = {
  tank:   { label: "Tank",       short: "TANK", color: "#38bdf8", glyph: "\u{1F6E1}" },
  healer: { label: "Healer",     short: "HEAL", color: "#34d399", glyph: "✚" },
  melee:  { label: "Melee DPS",  short: "MELEE", color: "#fb923c", glyph: "⚔" },
  ranged: { label: "Ranged DPS", short: "RANGED", color: "#fbbf24", glyph: "\u{1F3F9}" },
};

export const ROLE_ORDER: RoleKey[] = ["tank", "healer", "melee", "ranged"];

// role_label in the DB is canonical ("Tank" | "Healer" | "Melee DPS" |
// "Ranged DPS") but older rows carry a bare "DPS", and PUG rows can be null.
// Fall back to the class so nobody lands in the board without a role.
export function normalizeRole(
  roleLabel: string | null | undefined,
  className?: string | null,
): RoleKey {
  const r = (roleLabel || "").trim().toLowerCase();
  if (r.indexOf("tank") >= 0) return "tank";
  if (r.indexOf("heal") >= 0) return "healer";
  if (r.indexOf("melee") >= 0) return "melee";
  if (r.indexOf("ranged") >= 0 || r.indexOf("caster") >= 0) return "ranged";
  if (r === "dps" || r.endsWith(" dps")) return classIsRanged(className) ? "ranged" : "melee";
  return classDefaultRole(className);
}

const RANGED_CLASSES = ["Mage", "Warlock", "Hunter", "Priest"];

function classIsRanged(className?: string | null): boolean {
  return RANGED_CLASSES.indexOf(canonClass(className)) >= 0;
}

function classDefaultRole(className?: string | null): RoleKey {
  const c = canonClass(className);
  if (c === "Priest") return "healer";
  if (classIsRanged(c)) return "ranged";
  return "melee";
}

export function canonClass(name?: string | null): string {
  if (!name) return "";
  const key = name.replace(/\s+/g, "").toLowerCase();
  const known = ["Warrior", "Paladin", "Hunter", "Rogue", "Priest", "Shaman",
    "Mage", "Warlock", "Druid", "DeathKnight"];
  for (let i = 0; i < known.length; i++) {
    if (known[i].toLowerCase() === key) return known[i];
  }
  return name;
}

// ── Roster ───────────────────────────────────────────────────
// `tags`, `resource` and `scalesWith` are not typed in here; they arrive from
// the game's own spec table via gameData.withTraits(). This module used to
// hold a CLASS_ABILITIES map asserting that decurse is Mage and Druid and that
// six classes can interrupt, which is unanswerable once a second game version
// exists, and was wrong even for this one (a balance druid cannot interrupt).
export type Raider = {
  characterId: string;
  name: string;
  className: string;
  role: RoleKey;
  roleLabel: string;
  specKey?: string | null;
  specLabel?: string | null;
  userId?: string | null;
  isBench?: boolean;
  seatIndex?: number | null;
  // Filled from the database at load. Absent means the config has not arrived,
  // and a rule that asks for a tag will match nobody, which is the safe way
  // round: an empty duty is visible, a wrongly filled one is not.
  tags?: string[];
  resource?: string | null;
  scalesWith?: string[];
  traitsInferred?: boolean;
};

export const hasTag = (r: Raider, tag: string): boolean =>
  (r.tags || []).indexOf(tag) >= 0;

export const hasAnyTag = (r: Raider, tags: string[]): boolean =>
  tags.some((t) => hasTag(r, t));

// ── Plan shape ───────────────────────────────────────────────
export type SlotRule = {
  roles?: RoleKey[];        // any-of
  classes?: string[];       // any-of
  // Ability tags from game_composition_tag, any-of. A duty that several
  // spells satisfy lists them all: cleansing poison is Abolish Poison OR
  // Cure Poison OR the totem OR Cleanse, and which classes those belong to is
  // the database's business, not this file's.
  tags?: string[];
  preferRoles?: RoleKey[];  // sorted first, never required
  preferClasses?: string[];
};

// A duty can carry a second field. The real sheets use two flavours:
//   raider — Misdirection onto a tank, Innervate onto a caster
//   text   — Decurse "Group 5" / "Melee", Healing "Raid" / "Tank"
export type SlotTarget =
  | { kind: "raider"; label: string; rule: SlotRule; roleRef?: string }
  // `def` is the starting value. Without it every text target defaulted to
  // options[0], which made all five healers read "Tank" including the raid
  // healers. A default has to belong to the role, not to the option list.
  | { kind: "text"; label: string; options: string[]; def?: string };

export type DutySlot = {
  key: string;
  label: string;
  short: string;            // what the board prints, kept tiny
  // One row can carry several marks: a tank often holds skull AND moon, and
  // writing that as two rows duplicates the person. `marker` stays for the
  // single-mark case the templates use; `markers` wins when both are set.
  marker?: number | null;   // WoW raid target index 0-7
  markers?: number[];
  note?: string;
  rule: SlotRule;
  // Bind a slot to a night role and it shows the same person on every boss
  // until someone overrides that one slot.
  roleKey?: string;
  target?: SlotTarget;
};

export type LaneAccent = "tank" | "healer" | "dps" | "utility" | "control";

export type DutyLane = {
  key: string;
  label: string;
  accent: LaneAccent;
  note?: string;
  slots: DutySlot[];
};

// "general" is the night-wide box, rendered once at the top rather than
// repeated on every boss: innervates, soulstones, curse assignments. "trash"
// is the marker and crowd-control sheet for a wing.
export type SectionKind = "general" | "trash" | "boss";

export type BossPlan = {
  key: string;
  name: string;
  kind: SectionKind;
  instance?: string | null;   // wing label on a chained night (TK + SSC)
  note?: string;
  // The bullets a lead writes next to a fight: "run out with parasite",
  // "never stand in front of the boss". Real sheets carry more of these than
  // they carry assignments, and they were the one thing this board dropped.
  notes?: string[];
  lanes: DutyLane[];
};

export const LANE_ACCENT: Record<LaneAccent, string> = {
  tank: "#38bdf8", healer: "#34d399", dps: "#fb923c",
  utility: "#a78bfa", control: "#f59e0b",
};

// Every marker on a row, in game order, however it was declared.
export function slotMarkers(slot: { marker?: number | null; markers?: number[] }): number[] {
  if (slot.markers && slot.markers.length > 0) {
    return slot.markers.slice().sort((a, b) => a - b);
  }
  return slot.marker == null ? [] : [slot.marker];
}

export function slug(s: string): string {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

// ── Carrying a sheet forward ─────────────────────────────────
// Next week's raid copies last week's sheet, but the roster moves: the main
// tank is on holiday, two mages did not sign. A copied assignment therefore
// points at people who may not be here.
//
// The catch is that an absent player is not in the roster, so we no longer
// know their class or role, and "closest match" needs both. So a saved sheet
// carries a thin snapshot of everyone it named. It is the only reason the
// stored state holds anything beyond ids.
export type AbsentHint = { name: string; className: string; role: RoleKey };
export type RosterSnapshot = Record<string, AbsentHint>;

export function snapshotOf(roster: Raider[]): RosterSnapshot {
  const out: RosterSnapshot = {};
  roster.forEach((r) => {
    out[r.characterId] = { name: r.name, className: r.className, role: r.role };
  });
  return out;
}

// Who should take over. Same role matters more than same class: a druid tank
// standing in for a paladin tank is a better answer than a druid healer.
export function closestMatch(
  candidates: Raider[],
  hint: AbsentHint | null,
  rule: SlotRule,
  load: Record<string, number>,
): Raider | null {
  const score = (c: Raider) => {
    if (!hint) return 0;
    return (c.role === hint.role ? 2 : 0)
         + (canonClass(c.className) === canonClass(hint.className) ? 1 : 0);
  };
  return candidates.slice().sort((a, b) => {
    const sa = score(a), sb = score(b);
    if (sa !== sb) return sb - sa;
    const pa = rulePreference(a, rule), pb = rulePreference(b, rule);
    if (pa !== pb) return pa - pb;
    const la = load[a.characterId] || 0, lb = load[b.characterId] || 0;
    if (la !== lb) return la - lb;
    return a.name.localeCompare(b.name);
  })[0] || null;
}

// ── Eligibility ──────────────────────────────────────────────
export function isEligible(r: Raider, rule: SlotRule): boolean {
  if (r.isBench) return false;
  if (rule.roles && rule.roles.indexOf(r.role) < 0) return false;
  if (rule.classes && rule.classes.indexOf(canonClass(r.className)) < 0) return false;
  if (rule.tags && !hasAnyTag(r, rule.tags)) return false;
  return true;
}

export function eligibleFor(roster: Raider[], rule: SlotRule): Raider[] {
  return roster.filter((r) => isEligible(r, rule));
}

// Lower sorts first: an exact class preference beats a role preference.
export function rulePreference(r: Raider, rule: SlotRule): number {
  if (rule.preferClasses) {
    const i = rule.preferClasses.indexOf(r.className);
    if (i >= 0) return i;
  }
  if (rule.preferRoles && rule.preferRoles.indexOf(r.role) >= 0) return 50;
  return 100;
}

// ── Board ────────────────────────────────────────────────────
// Where a filled slot came from. The board prints this so a lead can tell a
// night-wide decision from a one-boss exception at a glance.
export type SlotSource = "auto" | "night" | "override";

// Structural shape of a resolved night role. Declared here rather than
// imported so assignmentModel stays the bottom of the dependency graph.
export type BoundRole = {
  raider: Raider | null;
  source: "auto" | "depth" | "manual" | "replaced";
  replacedName?: string | null;
  target: string | null;
  targetRaider: Raider | null;
  targetManual: boolean;
};

export type SlotAssignment = {
  slot: DutySlot;
  raider: Raider | null;
  eligible: Raider[];
  source: SlotSource;
  // Set when a carried-over sheet named someone who is not in this raid, so
  // the board can say "this was Aelisi" instead of quietly changing hands.
  replacedName?: string | null;
  target: string | null;
  targetRaider: Raider | null;
  targetSource: SlotSource;
  // True when this raider holds another slot on the SAME boss.
  doubled?: boolean;
};

export type LaneAssignment = { lane: DutyLane; slots: SlotAssignment[] };
export type BossAssignment = {
  boss: BossPlan;
  lanes: LaneAssignment[];
  // Lanes switched off for this boss, kept so the board can offer them back.
  hiddenLanes: { key: string; label: string }[];
  hiddenSlots: number;
};

// Overrides are keyed "<bossKey>::<laneKey>::<slotKey>" -> characterId | ""
// ("" means the raid lead deliberately emptied the slot).
export type OverrideMap = Record<string, string>;

// Not every boss needs every lane. A pull with nothing to interrupt should
// not print an interrupt rotation, and a fight with no curses should not ask
// three mages to stand by. Switching one off is a per-boss decision like any
// other override, keyed the same way: a lane key hides the whole lane, a slot
// id hides one row. Hidden duties are skipped entirely, so they also stop
// consuming people and stop counting toward anyone's duty load.
export type DisabledMap = Record<string, boolean>;

export const slotId = (bossKey: string, laneKey: string, slotKey: string) =>
  `${bossKey}::${laneKey}::${slotKey}`;
export const laneId = (bossKey: string, laneKey: string) =>
  `${bossKey}::${laneKey}`;
export const targetId = (id: string) => `${id}::target`;

export function buildBoard(
  plan: BossPlan[],
  roster: Raider[],
  overrides: OverrideMap,
  nightRoles?: Record<string, BoundRole>,
  disabled?: DisabledMap,
  snapshot: RosterSnapshot = {},
): BossAssignment[] {
  const resolved = nightRoles || {};
  const off = disabled || {};
  const nightLoad: Record<string, number> = {};
  roster.forEach((r) => { nightLoad[r.characterId] = 0; });

  return plan.map((boss) => {
    const usedThisBoss: Record<string, boolean> = {};
    const bossCount: Record<string, number> = {};
    const hiddenLanes: { key: string; label: string }[] = [];
    let hiddenSlots = 0;

    const lanes: LaneAssignment[] = boss.lanes.filter((lane) => {
      if (!off[laneId(boss.key, lane.key)]) return true;
      hiddenLanes.push({ key: lane.key, label: lane.label });
      return false;
    }).map((lane) => {
      const slots: SlotAssignment[] = lane.slots.filter((slot) => {
        if (!off[slotId(boss.key, lane.key, slot.key)]) return true;
        hiddenSlots++;
        return false;
      }).map((slot) => {
        const eligible = eligibleFor(roster, slot.rule);
        const key = slotId(boss.key, lane.key, slot.key);
        const bound = slot.roleKey ? resolved[slot.roleKey] : null;

        let raider: Raider | null = null;
        let source: SlotSource = "auto";
        let replacedName: string | null = null;

        const override = overrides[key];
        const overrideRaider = override
          ? roster.filter((r) => r.characterId === override)[0] || null
          : null;
        // A one-boss exception whose player is not here falls back to the
        // standing plan rather than emptying, since the night role already
        // resolved to whoever is covering that job tonight.
        const overrideAbsent = !!override && !overrideRaider;

        if (override !== undefined && !overrideAbsent) {
          raider = overrideRaider;
          source = "override";
        } else if (bound) {
          // A night role decides for every boss at once, which is the whole
          // point of it: no per-boss reshuffling of who main tanks.
          raider = bound.raider;
          source = bound.source === "auto" ? "auto" : "night";
          if (bound.replacedName) replacedName = bound.replacedName;
          if (overrideAbsent && override) {
            replacedName = (snapshot[override] || {}).name || replacedName;
          }
        } else {
          const fresh = eligible.filter((r) => !usedThisBoss[r.characterId]);
          const pool = fresh.length > 0 ? fresh : eligible;
          raider = pool.slice().sort((a, b) => {
            const pa = rulePreference(a, slot.rule);
            const pb = rulePreference(b, slot.rule);
            if (pa !== pb) return pa - pb;
            const la = nightLoad[a.characterId] || 0;
            const lb = nightLoad[b.characterId] || 0;
            if (la !== lb) return la - lb;
            return a.name.localeCompare(b.name);
          })[0] || null;
          if (overrideAbsent && override) {
            const hint = snapshot[override] || null;
            replacedName = hint ? hint.name : null;
            raider = closestMatch(pool, hint, slot.rule, nightLoad) || raider;
          }
        }

        if (raider) {
          usedThisBoss[raider.characterId] = true;
          bossCount[raider.characterId] = (bossCount[raider.characterId] || 0) + 1;
          nightLoad[raider.characterId] = (nightLoad[raider.characterId] || 0) + 1;
        }

        // Target, same layering.
        let target: string | null = null;
        let targetRaider: Raider | null = null;
        let targetSource: SlotSource = "auto";
        if (slot.target) {
          const tOverride = overrides[targetId(key)];
          if (tOverride !== undefined) {
            target = tOverride || null;
            targetSource = "override";
          } else if (bound) {
            target = bound.target;
            targetSource = bound.targetManual ? "night" : "auto";
          }
          if (slot.target.kind === "raider" && target) {
            targetRaider = roster.filter((r) => r.characterId === target)[0] || null;
            if (!targetRaider) {
              // Misdirecting onto someone who did not sign. Point it at the
              // closest body instead of leaving the row half-written.
              const hint = snapshot[target] || null;
              targetRaider = closestMatch(
                eligibleFor(roster, slot.target.rule), hint, slot.target.rule, nightLoad);
              target = targetRaider ? targetRaider.characterId : null;
              if (!replacedName && hint) replacedName = hint.name;
            }
          }
        }

        return {
          slot, raider, eligible, source, replacedName,
          target, targetRaider, targetSource,
        };
      });
      return { lane, slots };
    });

    // Second pass: a clash is only knowable once the whole boss is filled.
    lanes.forEach((l) => l.slots.forEach((s) => {
      s.doubled = !!s.raider && (bossCount[s.raider.characterId] || 0) > 1;
    }));

    // A lane whose every row was hidden one at a time reads as hidden too,
    // otherwise an empty titled box sits there meaning nothing.
    const kept = lanes.filter((l) => {
      if (l.slots.length > 0) return true;
      hiddenLanes.push({ key: l.lane.key, label: l.lane.label });
      return false;
    });

    return { boss, lanes: kept, hiddenLanes, hiddenSlots };
  });
}

// Slots where the fill had to double someone up, per boss. The board surfaces
// these so a lead can resolve them instead of finding out mid-pull.
export function clashesFor(board: BossAssignment[]): Record<string, number> {
  const out: Record<string, number> = {};
  board.forEach((b) => {
    const names: Record<string, boolean> = {};
    b.lanes.forEach((l) => l.slots.forEach((s) => {
      if (s.doubled && s.raider) names[s.raider.characterId] = true;
    }));
    const n = Object.keys(names).length;
    if (n > 0) out[b.boss.key] = n;
  });
  return out;
}

// ── Per-player view ──────────────────────────────────────────
export type MyDuty = {
  bossKey: string;
  bossName: string;
  instance?: string | null;
  laneLabel: string;
  laneAccent: LaneAccent;
  slotLabel: string;
  marker?: number | null;
  note?: string;
};

export function dutiesFor(board: BossAssignment[], characterId: string | null): MyDuty[] {
  if (!characterId) return [];
  const out: MyDuty[] = [];
  board.forEach((b) => {
    b.lanes.forEach((l) => {
      l.slots.forEach((s) => {
        if (s.raider && s.raider.characterId === characterId) {
          out.push({
            bossKey: b.boss.key, bossName: b.boss.name, instance: b.boss.instance,
            laneLabel: l.lane.label, laneAccent: l.lane.accent,
            slotLabel: s.slot.label, marker: s.slot.marker,
            note: s.slot.note || l.lane.note,
          });
        }
      });
    });
  });
  return out;
}

// How many duties each raider carries across the night.
export function loadByRaider(board: BossAssignment[]): Record<string, number> {
  const out: Record<string, number> = {};
  board.forEach((b) => b.lanes.forEach((l) => l.slots.forEach((s) => {
    if (s.raider) out[s.raider.characterId] = (out[s.raider.characterId] || 0) + 1;
  })));
  return out;
}
